import * as React from 'react';
import { LocationsPage, LocationsSummary } from 'components/location/model/Location';

interface IPage {
    page: LocationsPage;
}

interface IConfiguration {
    initialState?: LocationsSummary;
    perPage: number;
}

interface IPagination {
    nextKey?: string;
    hasNextPage: boolean;
    isLoading: boolean;
}

export function usePagination<T extends IPage>(configuration: IConfiguration) {
    const [pagination, setPagination] = React.useState<IPagination>({
        nextKey: configuration.initialState?.nextKey,
        hasNextPage: Boolean(configuration.initialState?.nextKey),
        isLoading: false
    });

    React.useEffect(() => {
        if (configuration.initialState) {
            setPagination({
                nextKey: configuration.initialState.nextKey,
                hasNextPage: Boolean(configuration.initialState.nextKey),
                isLoading: false
            });
        }
    }, [configuration.initialState]);

    const getParams = React.useCallback(
        (nextKey?: string) => {
            const params: Record<string, string> = {
                limit: String(configuration.perPage),
                state: 'ACTIVE',
                services: 'pre_order'
            };
            if (nextKey) {
                params.startKey = nextKey;
            }
            return params;
        },
        [configuration.perPage]
    );

    const loadMoreItems = React.useCallback(
        async (fn: (params: Record<string, string>) => Promise<T> | Promise<null>) => {
            if (!pagination.isLoading && pagination.hasNextPage) {
                setPagination(p => ({ ...p, isLoading: true }));
                try {
                    const data = await fn(getParams(pagination.nextKey));
                    if (data) {
                        setPagination({
                            nextKey: data.page.nextKey,
                            hasNextPage: Boolean(data.page.nextKey),
                            isLoading: false
                        });

                        return Promise.resolve(data);
                    }
                    setPagination(p => ({ ...p, isLoading: false }));
                } catch (err) {
                    setPagination(p => ({ ...p, isLoading: false }));
                    return Promise.reject(err);
                }
            }
        },
        [getParams, pagination]
    );

    return {
        loadMoreItems,
        isLoading: pagination.isLoading,
        hasNext: pagination.hasNextPage
    } as const;
}
